import { compact } from "lodash";
import * as parsel from "parsel-js";
import { astEqual, fixSelectorToExcludeType, stringify } from "./rulesetUtils";
/** To migrate the rulesets we split them in two: one targeting the type and one not */
function splitRuleset(ruleSet, targetNodeType, rulesToRemove, newDesc, nameA, nameB) {
    const parsedSelector = parsel.parse(ruleSet.selector);
    const fixedSelector = fixSelectorToExcludeType(parsedSelector, targetNodeType);
    const fixed = stringify(fixedSelector);
    // It could be that fixing the selector does nothing (e.g. if the selector is not targeting the type)
    if (astEqual(parsel.parse(fixed), parsedSelector)) {
        return null;
    }
    return [
        {
            ...ruleSet,
            name: nameA,
            uid: `${ruleSet.uid}-a`,
            description: newDesc,
            selector: fixed,
            rules: Object.fromEntries(Object.entries(ruleSet.rules).filter(([_key, rule]) => rulesToRemove.includes(rule.key))),
        },
        {
            ...ruleSet,
            name: nameB,
            uid: `${ruleSet.uid}-b`,
            description: newDesc,
            rules: Object.fromEntries(Object.entries(ruleSet.rules).filter(([_key, rule]) => !rulesToRemove.includes(rule.key))),
        },
    ];
}
/**
 * Run a migration to remove specific rules targeting a specific node type.
 * This is achieved by splitting the ruleset into two: one with the rules to remove and one with the rest.
 */
export function rulesetMigrationRemoveRulesFromNodeType(pcbLayoutRuleSets, targetNodeType, ruleKeysToMigrate, migrationDescInfoString, ruleNameASuffix) {
    return Object.fromEntries(compact(Object.entries(pcbLayoutRuleSets).reduce((prev, [key, ruleset]) => {
        const rulesetContainsTargetRule = ruleset &&
            Object.keys(ruleset.rules).some((ruleKey) => ruleKeysToMigrate.includes(ruleKey));
        // No target rules in this ruleset, no need for migration
        if (!rulesetContainsTargetRule) {
            return [...prev, [key, ruleset]];
        }
        const newDesc = [ruleset.description ?? "", migrationDescInfoString].join("\n");
        const nameA = `${ruleset.name} ${ruleNameASuffix}`;
        const nameB = `${ruleset.name} (other rules)`;
        const patched = splitRuleset(ruleset, targetNodeType, ruleKeysToMigrate, newDesc, nameA, nameB);
        if (!patched) {
            // No need to split since selector is the same, just return the original ruleset
            return [...prev, [key, ruleset]];
        }
        const patchedRule0 = patched[0];
        const patchedRule0Valid = patchedRule0 && Object.keys(patchedRule0.rules).length > 0 && patchedRule0.selector.length > 0;
        const patchedRule1 = patched[1];
        const patchedRule1Valid = patchedRule1 && Object.keys(patchedRule1.rules).length > 0 && patchedRule1.selector.length > 0;
        const prevWithoutCurrent = prev.filter((i) => i[0] !== key);
        if (patchedRule0Valid && patchedRule1Valid) {
            // Both splitted rules are valid, add both and remove original
            return [...prevWithoutCurrent, [patchedRule0.uid, patchedRule0], [patchedRule1.uid, patchedRule1]];
        }
        else if (patchedRule0Valid || patchedRule1Valid) {
            // Only one rule is valid, we don't change key/uid and keep only one
            const validOne = patchedRule0Valid ? patchedRule0 : patchedRule1;
            validOne.uid = ruleset.uid;
            validOne.name = ruleset.name;
            validOne.description = ruleset.description;
            return [...prevWithoutCurrent, [validOne.uid, validOne]];
        }
        // Don't add this ruleset, it has no valid rules
        return prev;
    }, [])));
}
//# sourceMappingURL=rulesetMigrations.js.map